import { useState } from 'react'
import { useProductProviders } from '../../hooks/useProductProviders'
import { Table } from '../Common/Table'
import { Pagination } from '../Common/Pagination'
import { Button } from '../Common/Button'
import { ProductProviderForm } from './ProductProviderForm'
import { ProductProviderImport } from './ProductProviderImport'

export function ProductProvidersList({ sidebarCollapsed, isDarkMode = false }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('name:asc')
  const [showForm, setShowForm] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingProvider, setEditingProvider] = useState(null)
  const importEnabled = localStorage.getItem('sfp_enable_transaction_import') === 'true'

  const [sortBy, sortOrder] = sort.split(':')
  const { providers, total, loading, createProvider, updateProvider, toggleActive, importProviders } = useProductProviders(page, 100, search, sortBy, sortOrder)

  const handleSave = async (formData, existingId) => {
    if (existingId) {
      return await updateProvider(existingId, formData)
    } else {
      return await createProvider(formData)
    }
  }

  const handleImport = async (data) => {
    try {
      const result = await importProviders(data)
      setShowImport(false)
      const { imported, skipped } = result || {}
      if (skipped?.length > 0) {
        alert(`Imported ${imported} new providers.\nSkipped ${skipped.length} duplicate${skipped.length > 1 ? 's' : ''} (already exist): ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? '…' : ''}`)
      }
    } catch (err) {
      alert(`Import failed: ${err.message}`)
    }
  }

  const handleExport = async () => {
    const XLSX = await import('xlsx')
    const exportData = providers.map(p => ({
      'Name': p.name || '',
      'Contact Count': p.contact_count ?? 0,
      'Active': p.active ? 'Yes' : 'No',
    }))
    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Product Providers')
    XLSX.writeFile(wb, 'Product Providers.xlsx')
  }

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'contact_count', label: 'Contact Count', render: (row) => row.contact_count ?? 0 },
    {
      key: 'active',
      label: 'Active',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleActive(row.id, row.active) }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${row.active ? 'bg-green-500' : 'bg-gray-300'}`}
          title={row.active ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.active ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setEditingProvider(row); setShowForm(true) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  return (
    <div className={`p-4 h-full flex flex-col gap-4 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="flex justify-between items-center gap-4 flex-shrink-0">
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => { setEditingProvider(null); setShowForm(true) }}>
            + Add Provider
          </Button>
          {importEnabled && (
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              ↑ Import Excel
            </Button>
          )}
          <Button variant="secondary" onClick={handleExport}>
            ↓ Export Excel
          </Button>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1) }}
            className="input-field text-sm"
          >
            <option value="name:asc">Name A → Z</option>
            <option value="name:desc">Name Z → A</option>
            <option value="created_at:desc">Date Created (Newest)</option>
            <option value="created_at:asc">Date Created (Oldest)</option>
          </select>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="input-field max-w-xs"
          />
        </div>
      </div>

      <div className={`overflow-hidden flex flex-col flex-1 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-auto flex-1">
          <Table
            columns={columns}
            data={providers}
            loading={loading}
            onRowClick={(row) => { setEditingProvider(row); setShowForm(true) }}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>

      <div className="flex-shrink-0">
        <Pagination
          currentPage={page}
          totalItems={total}
          pageSize={100}
          onPageChange={setPage}
          isDarkMode={isDarkMode}
        />
      </div>

      {showForm && (
        <ProductProviderForm
          initialData={editingProvider}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingProvider(null) }}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {showImport && (
        <ProductProviderImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  )
}
